import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Advertiser } from '@/models/media-network/Advertiser';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import AdvertiserDialog from './dialogs/AdvertiserDialog';

// The brand-wide list of who buys the ad space. It doesn't decide what plays — it answers "whose creative is
// this", which is what makes delivery reportable. See docs/ad-sales-plan.md in TouchTab.Promotion.
//
// Tags used to sit beside this list and now live under Promotions > Tags, because they're not advertiser-
// specific: Cross-Sell will tag its own content from the same list.
const Advertisers = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.mediaNetworkAdvertisers);
    const canUpdate = perms.canUpdate(Perm.mediaNetworkAdvertisers);
    const canDelete = perms.canDelete(Perm.mediaNetworkAdvertisers);

    const brandId = app.brandId ?? undefined;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Advertiser | null>(null);

    const { data: advertisers, isPending } = useQuery({
        queryKey: mediaNetworkKeys.advertisers(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.listAdvertisers(brandId!),
    });

    const { mutateAsync: removeAdvertiser } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.deleteAdvertiser(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistAdvertiserDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.advertisers(brandId ?? '') });
        },
        // The API refuses this while media still attributes to them. Surface its message rather than a
        // generic failure, because the message names the action to take instead.
        onError: (e: unknown) => toast({
            severity: 'error',
            message: errorMessage(e, strings.playlistAdvertiserDeleteError),
        }),
    });

    // The brand's veto over a TouchTab advertiser — the only thing it may change about one it does not own.
    const { mutateAsync: setBrandAdvertiser } = useMutation({
        mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
            mediaNetworkApi.setBrandAdvertiser(id, brandId!, { enabled }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.advertisers(brandId ?? '') });
        },
        onError: (e: unknown) => toast({
            severity: 'error',
            message: errorMessage(e, strings.playlistAdvertiserSaveError),
        }),
    });

    const advertiserColumns: GridColDef<Advertiser>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        {
            // Which tier a row came from, because it decides what can be done to it. A TouchTab advertiser is
            // shared with other clients and only TouchTab may rename or remove it.
            field: 'isPlatform', headerName: strings.advertiserSource, width: 130, sortable: false,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={params.row.isPlatform ? 'primary' : 'default'}
                        label={params.row.isPlatform ? strings.advertiserFromTouchTab : strings.advertiserYourOwn}
                    />
                </Stack>
            ),
        },
        { field: 'externalId', headerName: strings.playlistExternalId, flex: 1 },
        { field: 'mediaCount', headerName: strings.playlistMediaCount, width: 90 },
        {
            field: 'enabled', headerName: strings.enabled, width: 120,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={params.row.enabled ? 'success' : 'default'}
                        label={params.row.enabled ? strings.enabled : strings.disabled}
                    />
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', width: 170,
            // A TouchTab advertiser offers a different action entirely: not edit and delete, which belong to
            // TouchTab, but ACCEPT or DECLINE — the brand's own half of the arrangement. The API refuses the
            // other two anyway; hiding them here means nobody discovers that by being told no.
            getActions: (params: GridRowParams<Advertiser>) => params.row.isPlatform
                ? [
                    <Button
                        size="input"
                        variant="outlined"
                        color={params.row.enabled ? 'inherit' : 'primary'}
                        disabled={!canUpdate}
                        onClick={() => setBrandAdvertiser({ id: params.row.id, enabled: !params.row.enabled })}
                    >
                        {params.row.enabled ? strings.advertiserDecline : strings.advertiserAccept}
                    </Button>,
                ]
                : [
                    <DataGridEditButton
                        id={params.id.toString()}
                        onClick={() => { setEditing(params.row); setDialogOpen(true); }}
                        aria-label="edit advertiser"
                        readOnly={!canUpdate}
                    />,
                    <DataGridDeleteButton
                        id={params.id.toString()}
                        getWarningMessage={(id) => strings.playlistAdvertiserDeleteConfirm
                            .replace('{{name}}', advertisers?.find(a => a.id === id)?.name ?? '')}
                        onClick={(id) => removeAdvertiser(id)}
                        aria-label="delete advertiser"
                        hidden={!canDelete}
                    />,
                ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{strings.playlistAdvertisers}</Typography>
                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => { setEditing(null); setDialogOpen(true); }}
                            size="input"
                        >
                            {strings.playlistNewAdvertiser}
                        </Button>
                    )}
                </Stack>

                {/* Said once here rather than only in a failed delete, so the rule is discoverable before
                    someone tries. */}
                <Alert severity="info" variant="outlined">{strings.playlistAdvertiserInUse}</Alert>

                <Box sx={{ flex: 1, minHeight: 300 }}>
                    <DataGrid
                        rows={advertisers ?? []}
                        columns={advertiserColumns}
                        loading={isPending}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{ noRowsLabel: strings.playlistNoAdvertisers }}
                    />
                </Box>
            </Stack>

            <AdvertiserDialog
                open={dialogOpen}
                brandId={brandId}
                advertiser={editing}
                readOnly={!!editing && !canUpdate}
                onClose={() => setDialogOpen(false)}
            />
        </Box>
    );
};

export default Advertisers;
